import { Button, Form, useGetMany, useNotify, useRefresh } from "react-admin";
import { useCallback, useEffect, useState } from "react";
import { Backdrop, Box, CircularProgress, Dialog, DialogActions, DialogContent, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import SearchIcon from "@mui/icons-material/Search";
import { administrationRequests } from "api/administrationApi";
import { candidateRequests } from "api/candidateApi";
import dataProvider from "server/dataProvider";
import { useNotifyError } from "utils/useNotifyError";
import useConflictErrorModal from "server/components/Modals/useConflictErrorModal";
import AdditionalCapacityForm from "./AdditionalCapacityForm";
import AdditionalCapacityPreview from "./AdditionalCapacityPreview";
import ModalTitle, {
  withModalSx,
  modalButtonSx,
  modalCancelButtonSx,
  modalConfirmButtonSx,
} from "server/components/Modals/ModalTitle";

const isBlank = value => value === "" || value === null || value === undefined;

const toNullableNumber = value => (isBlank(value) ? null : Number(value));

const buildCapacities = (rows, specialities, isState) =>
  specialities
    .map((speciality, index) => ({ speciality, row: rows?.[index] }))
    .filter(({ row }) => !isBlank(row?.candidatesCapacity))
    .map(({ speciality, row }) => {
      const capacity = {
        specialityId: speciality.id,
        candidatesCapacity: Number(row.candidatesCapacity),
      };

      if (!isState) {
        return capacity;
      }

      return {
        ...capacity,
        maleCapacity: toNullableNumber(row.maleCapacity),
        femaleCapacity: toNullableNumber(row.femaleCapacity),
        zvoCapacity: toNullableNumber(row.zvoCapacity),
      };
    });

const hasQuotaValue = row => [row?.maleCapacity, row?.femaleCapacity, row?.zvoCapacity].some(quota => !isBlank(quota));

const isInvalidQuota = quota => !isBlank(quota) && (!Number.isInteger(quota) || quota < 0);

const validateCapacities = (capacities, specialities, rows, isState) => {
  if (isState) {
    const orphanIndex = specialities.findIndex(
      (_, index) => isBlank(rows?.[index]?.candidatesCapacity) && hasQuotaValue(rows?.[index]),
    );
    if (orphanIndex !== -1) {
      return `Въведете брой места за "${specialities[orphanIndex].name}" или изчистете квотите!`;
    }
  }

  if (!capacities.length) {
    return "Въведете брой места за поне една специалност!";
  }

  const invalid = capacities.find(
    capacity => !Number.isInteger(capacity.candidatesCapacity) || capacity.candidatesCapacity < 1,
  );
  if (invalid) {
    const name = specialities.find(speciality => speciality.id === invalid.specialityId)?.name;
    return `Броят места за "${name}" трябва да е цяло число, по-голямо от 0!`;
  }

  const invalidQuota = capacities.find(capacity =>
    [capacity.maleCapacity, capacity.femaleCapacity, capacity.zvoCapacity].some(isInvalidQuota),
  );
  if (invalidQuota) {
    const name = specialities.find(speciality => speciality.id === invalidQuota.specialityId)?.name;
    return `Квотите за "${name}" трябва да са цели неотрицателни числа!`;
  }

  const overQuota = capacities.find(capacity => {
    const quotaSum = (capacity.maleCapacity ?? 0) + (capacity.femaleCapacity ?? 0) + (capacity.zvoCapacity ?? 0);
    return quotaSum > capacity.candidatesCapacity;
  });

  if (overQuota) {
    const name = specialities.find(speciality => speciality.id === overQuota.specialityId)?.name;
    return `Сумата на квотите за "${name}" надвишава общия брой места!`;
  }

  return null;
};

const AdditionalCapacityDialog = ({ open, onClose, rankingId, specialityIds, isState }) => {
  const notify = useNotify();
  const notifyError = useNotifyError();
  const refresh = useRefresh();
  const { handleConflictError, ConflictErrorDialog } = useConflictErrorModal();

  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState(undefined);
  const [preview, setPreview] = useState(null);
  const [previewedCapacities, setPreviewedCapacities] = useState(null);
  const [summaries, setSummaries] = useState({});

  const { data: specialities, isLoading: specialitiesLoading } = useGetMany(
    administrationRequests.getSpecialitiesSelect(),
    { ids: specialityIds },
    { enabled: open && specialityIds.length > 0 },
  );

  // Helper info — the form works without it if loading fails.
  const fetchSummaries = useCallback(async () => {
    try {
      const entries = await Promise.all(
        specialityIds.map(async specialityId => {
          const { data } = await dataProvider.customRequest(
            candidateRequests.getCampaignRankingSummary(rankingId, specialityId),
            null,
            "GET",
          );
          return [specialityId, data];
        }),
      );
      setSummaries(Object.fromEntries(entries));
    } catch (error) {
      console.error("Failed to load per-speciality ranking summaries", error);
      setSummaries({});
    }
  }, [rankingId, specialityIds]);

  useEffect(() => {
    if (!open) return;
    fetchSummaries();
  }, [open, fetchSummaries]);

  const handleClose = useCallback(() => {
    setPreview(null);
    setPreviewedCapacities(null);
    setFormValues(undefined);
    setSummaries({});
    onClose();
  }, [onClose]);

  const handlePreview = useCallback(
    async values => {
      if (!specialities?.length) {
        notify("Няма заредени специалности за класирането!", { type: "error" });
        return;
      }

      const capacities = buildCapacities(values?.capacities, specialities, isState);
      const validationError = validateCapacities(capacities, specialities, values?.capacities, isState);

      if (validationError) {
        notify(validationError, { type: "error" });
        return;
      }

      setLoading(true);
      try {
        const { data } = await dataProvider.customRequest(
          candidateRequests.previewCampaignRankingAdditionalCapacity(rankingId),
          { capacities },
          "POST",
        );
        setFormValues(values);
        setPreviewedCapacities(capacities);
        setPreview(data?.specialities ?? []);
      } catch (error) {
        if (error?.properties?.length > 0) {
          handleConflictError(error);
        } else {
          notifyError(error);
        }
      } finally {
        setLoading(false);
      }
    },
    [specialities, isState, rankingId, notify, notifyError, handleConflictError],
  );

  const handleExecute = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await dataProvider.customRequest(
        candidateRequests.addCampaignRankingAdditionalCapacity(rankingId),
        { capacities: previewedCapacities },
        "POST",
      );

      const acceptedCount = (data?.specialities ?? []).reduce(
        (total, speciality) => total + (speciality.newlyAcceptedCandidates?.length ?? 0),
        0,
      );

      notify(`Успешно добавихте допълнителни места. Новоприети кандидати: ${acceptedCount}`, { type: "success" });
      refresh();
      handleClose();
    } catch (error) {
      if (error?.properties?.length > 0) {
        handleConflictError(error);
      } else {
        notifyError(error);
      }
    } finally {
      setLoading(false);
    }
  }, [rankingId, previewedCapacities, notify, notifyError, refresh, handleClose, handleConflictError]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        sx={withModalSx()}
      >
        <ModalTitle title="Допълнителни места към класиране" />
        {preview ? (
          <>
            <DialogContent dividers>
              <AdditionalCapacityPreview
                specialities={specialities ?? []}
                results={preview}
              />
              <Typography
                color="error"
                sx={{ fontWeight: "bold", marginTop: 2 }}
              >
                При потвърждение местата се записват към класирането, кандидатите по-горе се приемат и получават имейл.
                Действието е необратимо.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ display: "flex", justifyContent: "space-between", paddingX: 3, paddingY: 2 }}>
              <Box
                display="flex"
                gap={2}
              >
                <Button
                  label="Назад"
                  onClick={() => setPreview(null)}
                  variant="outlined"
                  size="medium"
                  disabled={loading}
                  sx={modalButtonSx}
                >
                  <ArrowBackIcon />
                </Button>
                <Button
                  label="Затвори"
                  onClick={handleClose}
                  variant="outlined"
                  color="error"
                  size="medium"
                  disabled={loading}
                  sx={modalCancelButtonSx}
                >
                  <CloseIcon />
                </Button>
              </Box>
              <Button
                label="Потвърди"
                onClick={handleExecute}
                variant="contained"
                size="medium"
                disabled={loading || !previewedCapacities?.length}
                sx={modalConfirmButtonSx}
              >
                <DoneIcon />
              </Button>
            </DialogActions>
          </>
        ) : (
          <Form
            defaultValues={formValues}
            onSubmit={handlePreview}
          >
            <DialogContent dividers>
              {specialitiesLoading ? (
                <CircularProgress size={30} />
              ) : (
                <AdditionalCapacityForm
                  specialities={specialities ?? []}
                  isState={isState}
                  summaries={summaries}
                />
              )}
              <Typography
                variant="body2"
                color="secondary"
                sx={{ fontStyle: "italic", marginTop: 2 }}
              >
                Изпращат се само специалностите с попълнен брой места. Проверката не записва нищо и не изпраща имейли —
                местата се добавят едва след потвърждение на следващата стъпка.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ display: "flex", justifyContent: "space-between", paddingX: 3, paddingY: 2 }}>
              <Button
                label="Затвори"
                onClick={handleClose}
                variant="outlined"
                color="error"
                size="medium"
                disabled={loading}
                sx={modalCancelButtonSx}
              >
                <CloseIcon />
              </Button>
              <Button
                label="Провери"
                type="submit"
                variant="contained"
                size="medium"
                disabled={loading || specialitiesLoading || !specialities?.length}
                sx={modalConfirmButtonSx}
              >
                <SearchIcon />
              </Button>
            </DialogActions>
          </Form>
        )}
        <Backdrop open={loading}>
          <CircularProgress
            color="primary"
            size={60}
          />
        </Backdrop>
      </Dialog>
      <ConflictErrorDialog />
    </>
  );
};

export default AdditionalCapacityDialog;
